import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";
import type {
  MismatchStatus,
  Transaction,
  TransactionMismatchDto,
} from "@boo/shared";
import {
  fetchMismatches as fetchMismatchesApi,
  resolveMismatch as resolveMismatchApi,
} from "../../../services/reconciliationApi";

interface ReconciliationState {
  isOpen: boolean;
  mismatches: TransactionMismatchDto[];
  isLoading: boolean;
  /** True once something was resolved since the modal opened. */
  hasResolved: boolean;
  open: () => void;
  close: () => void;
  fetchMismatches: () => Promise<void>;
  resolveMismatch: (
    id: string,
    action: MismatchStatus,
    editedData?: Partial<Transaction>,
  ) => Promise<void>;
}

export const useReconciliationStore = create<ReconciliationState>(
  (set, get) => ({
    isOpen: false,
    mismatches: [],
    isLoading: false,
    hasResolved: false,

    open() {
      // Reset to a loading state so the modal never flashes the "all resolved"
      // success view before the fetch completes.
      set({
        isOpen: true,
        isLoading: true,
        mismatches: [],
        hasResolved: false,
      });
    },

    close() {
      set({ isOpen: false });
    },

    async fetchMismatches() {
      set({ isLoading: true });
      try {
        set({ mismatches: await fetchMismatchesApi(), isLoading: false });
      } catch (err) {
        console.error("Failed to load mismatches:", err);
        set({ isLoading: false });
      }
    },

    async resolveMismatch(id, action, editedData) {
      await resolveMismatchApi(id, action, editedData);
      // Drop the resolved item so the list (and the empty/success state) updates.
      set({
        mismatches: get().mismatches.filter((m) => m.id !== id),
        hasResolved: true,
      });
    },
  }),
);

/** Object selector — wrapped in useShallow to avoid re-render loops. */
export const useReconciliationState = () =>
  useReconciliationStore(
    useShallow((s) => ({
      isOpen: s.isOpen,
      mismatches: s.mismatches,
      isLoading: s.isLoading,
    })),
  );
